'use client'

import type { FC } from 'react'
import {
  Award,
  BadgeCheck,
  BookOpen,
  Clock,
  GraduationCap,
  Languages,
  MapPin,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { TeamMember } from '@/types/doctor'
import {
  normalizeExpertise,
  hasExpertise,
  getDisplayName,
} from '@/types/doctor'
import { clinicIdentity } from '@/lib/clinic-identity'
import { trackAnalyticsEvent } from '@/lib/analytics'

/**
 * ClinicianProfileContent - the single shared profile template rendered
 * inside the /team bio modal for every clinician type.
 *
 * Content rules: every optional section (credentials, registrations,
 * philosophy, education, languages, expertise groups, publications, review
 * metadata) is omitted entirely when its data is absent - no placeholders,
 * no fallback dates. The practice location is sourced from clinicIdentity
 * only; no contact values are hard-coded here.
 */

export interface ClinicianProfileContentProps {
  member: TeamMember
  /** 'sm' = mobile sheet, 'lg' = desktop panel */
  size: 'sm' | 'lg'
  isBioExpanded: boolean
  onToggleBio: () => void
  onBookAppointment: () => void
}

const uniqueItems = (items: string[]): string[] => Array.from(new Set(items))

const formatReviewDate = (iso: string): string => {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

const ClinicianProfileContent: FC<ClinicianProfileContentProps> = ({
  member,
  size,
  isBioExpanded,
  onToggleBio,
  onBookAppointment,
}) => {
  const expertise = normalizeExpertise(member.expertise)
  const childrenExpertise = uniqueItems(expertise.children)
  const adultsExpertise = uniqueItems(expertise.adults)
  const showExpertise = hasExpertise(member)
  const displayName = getDisplayName(member)

  const sectionHeading = (label: string, Icon?: typeof Award) => (
    <h3
      className={`font-semibold text-white flex items-center ${
        size === 'sm' ? 'text-[15px] mb-2 gap-1.5' : 'text-lg mb-3 gap-2'
      }`}
    >
      {Icon && (
        <Icon
          className={`text-[hsl(188,86%,53%)] ${
            size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
          }`}
        />
      )}
      {label}
    </h3>
  )

  const pillList = (items: string[], keyPrefix: string) => (
    <div className={`flex flex-wrap ${size === 'sm' ? 'gap-1.5' : 'gap-2'}`}>
      {items.map((item, idx) => (
        <span
          key={`${keyPrefix}-${idx}`}
          className={`bg-white/10 backdrop-blur-sm text-white rounded-full border border-white/20 ${
            size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
          }`}
        >
          {item}
        </span>
      ))}
    </div>
  )

  const bulletList = (items: string[]) => (
    <ul className={size === 'sm' ? 'space-y-1.5' : 'space-y-2'}>
      {items.map((item, idx) => (
        <li
          key={idx}
          className={`text-[hsl(220,14%,96%)] leading-relaxed relative before:content-[''] before:absolute before:rounded-full before:bg-[hsl(188,86%,53%)] ${
            size === 'sm'
              ? 'text-xs pl-3.5 before:left-0 before:top-1.5 before:w-1.5 before:h-1.5'
              : 'text-sm pl-4 before:left-0 before:top-2 before:w-2 before:h-2'
          }`}
        >
          {item}
        </li>
      ))}
    </ul>
  )

  return (
    <div className={size === 'sm' ? 'space-y-5 pb-4' : 'space-y-6 pb-6'}>
      {/* ── Profile header ── */}
      <header>
        <h2
          className={`font-bold text-white leading-tight ${
            size === 'sm' ? 'text-[22px] mb-0.5' : 'text-4xl lg:text-5xl mb-2'
          }`}
        >
          {member.name}
        </h2>
        <p
          className={`text-[hsl(188,100%,56%)] font-medium mb-1 ${
            size === 'sm' ? 'text-base' : 'text-xl'
          }`}
        >
          {member.title}
        </p>
        <p
          className={`text-[hsl(216,12%,84%)] mb-1 ${
            size === 'sm' ? 'text-xs' : 'text-sm'
          }`}
        >
          {member.specialty}
        </p>
        {member.yearsExperience && (
          <p
            className={`text-[hsl(216,12%,84%)] flex items-center ${
              size === 'sm' ? 'text-xs gap-1.5' : 'text-sm gap-2'
            }`}
          >
            <Clock
              className={`text-[hsl(188,97%,56%)] ${
                size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'
              }`}
            />
            {member.yearsExperience} years of experience
          </p>
        )}
      </header>

      {/* ── Credentials (neutral bucket - see TeamMember docs) ── */}
      {member.credentials?.length ? (
        <section>
          {sectionHeading('Credentials', Award)}
          {pillList(member.credentials, 'cred')}
        </section>
      ) : null}

      {/* ── Registrations (only when confirmed data exists) ── */}
      {member.registrations?.length ? (
        <section>
          {sectionHeading('Registrations', BadgeCheck)}
          {pillList(member.registrations, 'reg')}
        </section>
      ) : null}

      {/* ── Biography ── */}
      <section>
        {sectionHeading('About')}
        <div className="relative">
          <p
            className={`text-[hsl(220,14%,96%)] transition-all duration-300 ${
              size === 'sm'
                ? 'text-[13px] leading-[1.65]'
                : 'text-base leading-relaxed'
            } ${!isBioExpanded ? (size === 'sm' ? 'line-clamp-4' : 'line-clamp-3') : ''}`}
          >
            {member.bio}
          </p>
          {member.bio && member.bio.length > (size === 'sm' ? 150 : 200) && (
            <button
              onClick={onToggleBio}
              aria-expanded={isBioExpanded}
              className={`text-[hsl(188,86%,53%)] hover:text-[hsl(188,86%,70%)] active:text-[hsl(188,86%,80%)] transition-colors font-medium ${
                size === 'sm' ? 'mt-1.5 text-xs' : 'mt-2 text-sm'
              }`}
            >
              {isBioExpanded ? 'See less' : 'See more'}
            </button>
          )}
        </div>
      </section>

      {/* ── Approach to care ── */}
      {member.philosophy ? (
        <section>
          {sectionHeading('Treatment Philosophy')}
          <p
            className={`text-[hsl(220,14%,96%)] italic border-[hsl(188,86%,53%)] ${
              size === 'sm'
                ? 'text-[13px] leading-[1.65] border-l-[3px] pl-3'
                : 'text-base leading-relaxed border-l-4 pl-4'
            }`}
          >
            &ldquo;{member.philosophy}&rdquo;
          </p>
        </section>
      ) : null}

      {/* ── Education ── */}
      {member.education?.length ? (
        <section>
          {sectionHeading('Education', GraduationCap)}
          {bulletList(member.education)}
        </section>
      ) : null}

      {/* ── Languages ── */}
      {member.languagesKnown?.length ? (
        <section>
          {sectionHeading('Languages Spoken', Languages)}
          <div
            className={`flex flex-wrap ${size === 'sm' ? 'gap-1.5' : 'gap-2'}`}
          >
            {member.languagesKnown.map((lang, idx) => (
              <span
                key={idx}
                className={`bg-[hsl(189,94%,43%)]/20 text-[hsl(187,100%,86%)] rounded-lg border border-[hsl(188,86%,53%)]/30 ${
                  size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                }`}
              >
                {lang}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Areas of expertise ── */}
      {showExpertise ? (
        <section>
          {sectionHeading('Areas of Expertise')}

          {childrenExpertise.length > 0 && (
            <div className={size === 'sm' ? 'mb-3' : 'mb-4'}>
              <h4
                className={`font-semibold text-[hsl(188,86%,53%)] ${
                  size === 'sm' ? 'text-xs mb-1.5' : 'text-sm mb-2'
                }`}
              >
                Children &amp; Adolescents
              </h4>
              <div
                className={`flex flex-wrap ${size === 'sm' ? 'gap-1.5' : 'gap-2'}`}
              >
                {childrenExpertise.map((area, idx) => (
                  <span
                    key={`child-${idx}`}
                    className={`bg-[hsl(239,84%,67%)]/20 text-[hsl(232,100%,89%)] rounded-lg border border-[hsl(239,83%,66%)]/30 ${
                      size === 'sm'
                        ? 'px-2.5 py-1 text-xs'
                        : 'px-3 py-1.5 text-sm'
                    }`}
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}

          {adultsExpertise.length > 0 && (
            <div>
              <h4
                className={`font-semibold text-[hsl(188,86%,53%)] ${
                  size === 'sm' ? 'text-xs mb-1.5' : 'text-sm mb-2'
                }`}
              >
                Adults
              </h4>
              <div
                className={`flex flex-wrap ${size === 'sm' ? 'gap-1.5' : 'gap-2'}`}
              >
                {adultsExpertise.map((area, idx) => (
                  <span
                    key={`adult-${idx}`}
                    className={`bg-[hsl(142,71%,45%)]/20 text-[hsl(142,77%,73%)] rounded-lg border border-[hsl(142,69%,58%)]/30 ${
                      size === 'sm'
                        ? 'px-2.5 py-1 text-xs'
                        : 'px-3 py-1.5 text-sm'
                    }`}
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      ) : null}

      {/* ── Publications & research ── */}
      {member.publications?.length ? (
        <section>
          {sectionHeading('Publications & Research', BookOpen)}
          {bulletList(member.publications)}
        </section>
      ) : null}

      {/* ── Practice location (central clinic identity only) ── */}
      <section>
        {sectionHeading('Practice Location', MapPin)}
        <p
          className={`text-[hsl(220,14%,96%)] font-medium ${
            size === 'sm' ? 'text-[13px]' : 'text-base'
          }`}
        >
          {clinicIdentity.name}
        </p>
        <p
          className={`text-[hsl(216,12%,84%)] ${
            size === 'sm' ? 'text-xs mt-0.5' : 'text-sm mt-1'
          }`}
        >
          {clinicIdentity.locality}
        </p>
        <a
          href={clinicIdentity.maps.placeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackAnalyticsEvent('directions_click', {
              cta_location: 'clinician_profile',
            })
          }}
          className={`inline-block text-[hsl(188,86%,53%)] hover:text-[hsl(188,86%,70%)] transition-colors font-medium underline underline-offset-2 ${
            size === 'sm' ? 'mt-1.5 text-xs' : 'mt-2 text-sm'
          }`}
        >
          Get Directions
        </a>
      </section>

      {/* ── Review metadata (genuine reviews only; omitted otherwise) ── */}
      {member.review ? (
        <p
          className={`text-[hsl(216,12%,70%)] ${
            size === 'sm' ? 'text-[11px]' : 'text-xs'
          }`}
        >
          Reviewed by {member.review.reviewedBy}. Last reviewed{' '}
          {formatReviewDate(member.review.reviewedAt)}.
        </p>
      ) : null}

      {/* ── Appointment CTA ── */}
      <div
        className={`border-t border-white/10 ${size === 'sm' ? 'pt-4' : 'pt-6'}`}
      >
        <Button
          className={`w-full bg-[hsl(189,94%,43%)] hover:bg-[hsl(192,91%,36%)] active:bg-[hsl(194,88%,30%)] text-[hsl(0,0%,10%)] font-semibold rounded-xl shadow-lg shadow-[hsl(189,94%,43%)]/20 transition-all ${
            size === 'sm'
              ? 'py-4 text-sm'
              : 'py-6 text-base hover:shadow-xl hover:shadow-[hsl(189,94%,43%)]/30'
          }`}
          onClick={onBookAppointment}
        >
          Schedule an Appointment with {displayName}
        </Button>
      </div>
    </div>
  )
}

export default ClinicianProfileContent
